import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { TrendingUp, ArrowUpRight, IndianRupee, Package, GitBranch, Users, Wallet, Award, Zap, BarChart3, Rocket } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import NoticeBoard from '../components/NoticeBoard';


// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200">
                <p className="font-semibold text-gray-900 mb-3 text-sm">{label}</p>
                <div className="space-y-1.5">
                    {payload.map((entry, index) => (
                        <p key={index} className="text-xs font-medium" style={{ color: entry.stroke }}>
                            {entry.name}: {entry.value?.toLocaleString('en-IN')}
                        </p>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

/* ── Single Value Stat Card ── */
const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick, isCurrency = false }) => (
    <div
        onClick={onClick}
        className={`group relative overflow-hidden bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-all duration-300 min-h-[140px] sm:min-h-[160px] flex flex-col ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : ''}`}
    >
        <div className={`absolute -right-4 -top-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${color.bg}`} />
        <div className="relative flex flex-col h-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg ${color.iconBg} text-white`}>
                    <Icon size={20} className="sm:hidden" />
                    <Icon size={24} className="hidden sm:block" />
                </div>
                {onClick && (
                    <div className={`p-1 sm:p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${color.lightBg}`}>
                        <ArrowUpRight size={14} className={`${color.text} sm:hidden`} />
                        <ArrowUpRight size={16} className={`${color.text} hidden sm:block`} />
                    </div>
                )}
            </div>
            <div className="mt-auto">
                <p className="text-[10px] sm:text-sm font-medium text-gray-500 mb-0.5 sm:mb-1">{title}</p>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 tracking-tight">
                    {isCurrency ? '₹' : ''} {value}
                </h2>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1 font-medium italic leading-tight">{subtitle}</p>
            </div>
        </div>
    </div>
);

/* ── Dual Value Card (Left / Right in one card) ── */
const DualStatCard = ({ title, leftLabel, leftValue, rightLabel, rightValue, subtitle, icon: Icon, color, onClick, isCurrency = false }) => (
    <div
        onClick={onClick}
        className={`group relative overflow-hidden bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-all duration-300 min-h-[140px] sm:min-h-[160px] flex flex-col ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : ''}`}
    >
        <div className={`absolute -right-4 -top-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${color.bg}`} />
        <div className="relative flex flex-col h-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg ${color.iconBg} text-white`}>
                    <Icon size={20} className="sm:hidden" />
                    <Icon size={24} className="hidden sm:block" />
                </div>
                <p className="text-[10px] sm:text-sm font-bold text-gray-700">{title}</p>
            </div>
            <div className="mt-auto grid grid-cols-2 gap-2 sm:gap-4">
                <div className={`p-2 sm:p-3 rounded-xl ${color.lightBg}`}>
                    <p className="text-[9px] sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{leftLabel}</p>
                    <p className="text-sm sm:text-lg font-black text-gray-900 mt-0.5 sm:mt-1">{isCurrency ? '₹' : ''}{leftValue}</p>
                </div>
                <div className={`p-2 sm:p-3 rounded-xl ${color.lightBg}`}>
                    <p className="text-[9px] sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{rightLabel}</p>
                    <p className="text-sm sm:text-lg font-black text-gray-900 mt-0.5 sm:mt-1">{isCurrency ? '₹' : ''}{rightValue}</p>
                </div>
            </div>
            {subtitle && <p className="text-[10px] sm:text-xs text-gray-400 mt-2 font-medium italic leading-tight">{subtitle}</p>}
        </div>
    </div>
);

/* ── Triple Value Card (Direct / Left / Right) ── */
const TripleStatCard = ({ title, values, subtitle, icon: Icon, color, onClick }) => (
    <div
        onClick={onClick}
        className={`group relative overflow-hidden bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-all duration-300 min-h-[140px] sm:min-h-[160px] flex flex-col ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : ''}`}
    >
        <div className={`absolute -right-4 -top-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${color.bg}`} />
        <div className="relative flex flex-col h-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg ${color.iconBg} text-white`}>
                    <Icon size={20} className="sm:hidden" />
                    <Icon size={24} className="hidden sm:block" />
                </div>
                <p className="text-[10px] sm:text-sm font-bold text-gray-700">{title}</p>
            </div>
            <div className="mt-auto grid grid-cols-3 gap-1.5 sm:gap-2">
                {values.map((v, i) => (
                    <div key={i} className={`p-1.5 sm:p-2.5 rounded-xl text-center ${color.lightBg}`}>
                        <p className="text-[8px] sm:text-[10px] font-semibold text-gray-500 uppercase tracking-wide truncate">{v.label}</p>
                        <p className="text-sm sm:text-lg font-black text-gray-900 mt-0.5">{v.value}</p>
                    </div>
                ))}
            </div>
            {subtitle && <p className="text-[10px] sm:text-xs text-gray-400 mt-2 font-medium italic text-center leading-tight">{subtitle}</p>}
        </div>
    </div>
);

const UserDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timePeriod, setTimePeriod] = useState('30days');

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('authToken');
            if (!token) {
                console.log('No auth token found, skipping dashboard fetch');
                setLoading(false);
                return;
            }

            try {
                const dashboardData = await apiService.getDashboard();
                if (dashboardData.success) {
                    setStats(dashboardData);
                }
            } catch (error) {
                console.error("Failed to fetch dashboard data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    // ── Extract data from API response ──
    const walletBalance = parseFloat(stats?.wallet_balance || 0).toLocaleString('en-IN');
    const shoppingBalance = parseFloat(stats?.shopping_balance || 0).toLocaleString('en-IN');
    const totalEarnings = parseFloat(stats?.total_earnings || 0).toLocaleString('en-IN');

    const leftCF = parseFloat(stats?.left_cf || 0).toFixed(2);
    const rightCF = parseFloat(stats?.right_cf || 0).toFixed(2);

    const directCount = stats?.direct_count || 0;
    const leftCount = stats?.left_count || 0;
    const rightCount = stats?.right_count || 0;

    const matchingBonus = parseFloat(stats?.matching_bonus || 0).toLocaleString('en-IN');
    const performanceBonus = parseFloat(stats?.performance_bonus || 0).toLocaleString('en-IN');

    const runningLeftPV = parseFloat(stats?.running_left_pv || 0).toFixed(2);
    const runningRightPV = parseFloat(stats?.running_right_pv || 0).toFixed(2);

    const boosterCfPv = parseFloat(stats?.booster_cf_pv || 0).toFixed(2);

    // Process Earnings for Chart (Grouped by date and type)
    const recentEarnings = stats?.recent_earnings || [];
    const groupedEarnings = {};

    recentEarnings.forEach(e => {
        const date = new Date(e.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
        if (!groupedEarnings[date]) {
            groupedEarnings[date] = { date, direct_income: 0, sponsor_income: 0, matching_income: 0, bonus_income: 0, total: 0 };
        }

        const type = e.source_type.toLowerCase();
        const amount = parseFloat(e.amount);

        if (type.includes('direct')) groupedEarnings[date].direct_income += amount;
        else if (type.includes('sponsor')) groupedEarnings[date].sponsor_income += amount;
        else if (type.includes('matching') || type === 'binary_payout') groupedEarnings[date].matching_income += amount;
        else if (type.includes('perf') || type.includes('eligib') || type === 'pv_match_bonus') groupedEarnings[date].bonus_income += amount;
        else groupedEarnings[date].other = (groupedEarnings[date].other || 0) + amount;

        groupedEarnings[date].total += amount;
    });

    const chartData = Object.values(groupedEarnings).reverse();

    // Grouping by type for Pie Chart
    const pieMap = {};
    recentEarnings.forEach(e => {
        pieMap[e.source_type] = (pieMap[e.source_type] || 0) + parseFloat(e.amount);
    });

    const pieData = Object.keys(pieMap).map(key => ({
        name: key,
        value: pieMap[key]
    }));

    if (pieData.length === 0) {
        pieData.push({ name: 'No Data', value: 1 });
    }

    const COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'];

    return (
        <Layout>
            <div className="space-y-8 animate-fadeIn pt-6">
                <NoticeBoard />
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Dashboard</h1>
                        <p className="text-gray-500 mt-1 font-medium">Monitoring your network & earnings in real-time</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                            <p className="text-xs text-gray-600 font-medium">Last Updated</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                    </div>
                </div>

                {/* ── Dashboard Cards Grid ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* 1) Main Wallet */}
                    <StatCard
                        title="Main Wallet"
                        value={walletBalance}
                        subtitle="Available to Withdraw"
                        icon={Wallet}
                        isCurrency={true}
                        color={{ iconBg: 'bg-indigo-500', bg: 'bg-indigo-500', text: 'text-indigo-600', lightBg: 'bg-indigo-50' }}
                        onClick={() => navigate('/income/wallet')}
                    />

                    {/* 2) Shopping Wallet */}
                    <StatCard
                        title="Shopping Wallet"
                        value={shoppingBalance}
                        subtitle="For Store Purchases"
                        icon={Package}
                        isCurrency={true}
                        color={{ iconBg: 'bg-blue-500', bg: 'bg-blue-500', text: 'text-blue-600', lightBg: 'bg-blue-50' }}
                        onClick={() => navigate('/products/shopping-wallet')}
                    />

                    {/* 3) Carry Forward (Left + Right in one card) */}
                    <DualStatCard
                        title="Carry Forward"
                        leftLabel="Left CF"
                        leftValue={leftCF}
                        rightLabel="Right CF"
                        rightValue={rightCF}
                        subtitle="Binary Carry Forward PV"
                        icon={GitBranch}
                        color={{ iconBg: 'bg-violet-600', bg: 'bg-violet-600', text: 'text-violet-600', lightBg: 'bg-violet-50' }}
                        onClick={() => navigate('/income/binary')}
                    />

                    {/* 4) Total Members (Direct, Left, Right) */}
                    <TripleStatCard
                        title="Total Members"
                        values={[
                            { label: 'Direct', value: directCount },
                            { label: 'Left', value: leftCount },
                            { label: 'Right', value: rightCount }
                        ]}
                        subtitle="Click to view network downlines"
                        icon={Users}
                        color={{ iconBg: 'bg-pink-500', bg: 'bg-pink-500', text: 'text-pink-600', lightBg: 'bg-pink-50' }}
                        onClick={() => navigate('/network/downlines')}
                    />

                    {/* 5) Matching Bonus */}
                    <StatCard
                        title="Matching Bonus"
                        value={parseFloat(stats?.matching_bonus || 0) > 0 ? matchingBonus : '—'}
                        subtitle="Latest Cycle Matching Income"
                        icon={Zap}
                        isCurrency={parseFloat(stats?.matching_bonus || 0) > 0}
                        color={{ iconBg: 'bg-amber-500', bg: 'bg-amber-500', text: 'text-amber-600', lightBg: 'bg-amber-50' }}
                        onClick={() => navigate('/income/matching')}
                    />

                    {/* 6) Performance Bonus */}
                    <StatCard
                        title="Performance Bonus"
                        value={parseFloat(stats?.performance_bonus || 0) > 0 ? performanceBonus : '—'}
                        subtitle="Latest Cycle Performance Bonus"
                        icon={Award}
                        isCurrency={parseFloat(stats?.performance_bonus || 0) > 0}
                        color={{ iconBg: 'bg-teal-500', bg: 'bg-teal-500', text: 'text-teal-600', lightBg: 'bg-teal-50' }}
                        onClick={() => navigate('/income/performance')}
                    />

                    {/* 7) Total Earnings */}
                    <StatCard
                        title="Total Earnings"
                        value={totalEarnings}
                        subtitle="Lifetime Income"
                        icon={IndianRupee}
                        isCurrency={true}
                        color={{ iconBg: 'bg-emerald-500', bg: 'bg-emerald-500', text: 'text-emerald-600', lightBg: 'bg-emerald-50' }}
                        onClick={() => navigate('/earnings')}
                    />

                    {/* 8) Running Cycle PV (Left + Right in one card) */}
                    <DualStatCard
                        title="Running Cycle PV"
                        leftLabel="Left PV"
                        leftValue={runningLeftPV}
                        rightLabel="Right PV"
                        rightValue={runningRightPV}
                        subtitle="Current cycle effective PV"
                        icon={BarChart3}
                        color={{ iconBg: 'bg-cyan-600', bg: 'bg-cyan-600', text: 'text-cyan-600', lightBg: 'bg-cyan-50' }}
                        onClick={() => navigate('/income/binary')}
                    />

                    {/* 9) Booster Income CF PV */}
                    <StatCard
                        title="Booster Income CF PV"
                        value={boosterCfPv}
                        subtitle="Booster Carry Forward PV"
                        icon={Rocket}
                        color={{ iconBg: 'bg-orange-500', bg: 'bg-orange-500', text: 'text-orange-600', lightBg: 'bg-orange-50' }}
                        onClick={() => navigate('/earnings')}
                    />
                </div>

                {/* Charts Area */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit">
                        {/* ... Chart Header ... */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                            <div>
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Earnings Overview</h3>
                                <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5">Track your income performance</p>
                            </div>
                            <div className="flex space-x-1 bg-gray-50 p-1 rounded-lg border border-gray-200 w-full sm:w-auto">
                                <button
                                    onClick={() => setTimePeriod('today')}
                                    className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-md transition-all ${timePeriod === 'today'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600'
                                        }`}
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => setTimePeriod('week')}
                                    className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-md transition-all ${timePeriod === 'week'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600'
                                        }`}
                                >
                                    Week
                                </button>
                                <button
                                    onClick={() => setTimePeriod('30days')}
                                    className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-md transition-all ${timePeriod === '30days'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600'
                                        }`}
                                >
                                    30 Days
                                </button>
                            </div>
                        </div>

                        <div className="h-[200px] sm:h-[220px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                                    <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                                    <Line type="monotone" dataKey="direct_income" stroke="#fbbf24" strokeWidth={3} name="Direct" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    <Line type="monotone" dataKey="sponsor_income" stroke="#f59e0b" strokeWidth={3} name="Sponsor" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    <Line type="monotone" dataKey="matching_income" stroke="#3b82f6" strokeWidth={3} name="Matching" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    <Line type="monotone" dataKey="bonus_income" stroke="#ec4899" strokeWidth={3} name="Bonus" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={3} name="Total Day" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div >

                    <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-6 sm:mb-8">
                            <div>
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Breakdown</h3>
                                <p className="text-gray-500 text-xs sm:text-sm mt-1">Source analysis</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-center h-[180px] sm:h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={70}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `₹ ${value.toLocaleString('en-IN')}`} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6">
                            {pieData.map((item, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] sm:text-xs text-gray-600 truncate">{item.name}</p>
                                        <p className="font-semibold text-gray-800 text-[10px] sm:text-xs">₹ {item.value.toLocaleString('en-IN')}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div >

                {/* Bottom Section: Side Bar Modules Alignment (Downline & Payouts) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Downline Activity */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-sm border border-gray-100 flex flex-col">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-900">Recent Downline</h3>
                                <p className="text-gray-500 text-[10px] sm:text-xs mt-1 font-medium">Network growth</p>
                            </div>
                            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                                <TrendingUp size={18} />
                            </div>
                        </div>
                        <div className="space-y-3 sm:space-y-4 flex-1">
                            {stats?.recent_downline && stats.recent_downline.length > 0 ? (
                                stats.recent_downline.map((member, idx) => (
                                    <div key={idx} className="flex items-center justify-between group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-black text-[10px] sm:text-xs group-hover:bg-indigo-600 group-hover:text-white transition-colors">{member.name.charAt(0)}</div>
                                            <div>
                                                <p className="text-xs sm:text-sm font-bold text-gray-900 leading-none mb-1">{member.name}</p>
                                                <p className="text-[9px] sm:text-[10px] font-bold text-green-600 uppercase tracking-wider">Level {member.level} • {member.status}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="h-32 sm:h-40 flex flex-col items-center justify-center text-gray-400">
                                    <Users size={28} className="mb-2 opacity-20" />
                                    <p className="text-[10px] font-medium">No recent activity</p>
                                </div>
                            )}
                        </div>
                        <button onClick={() => navigate('/downline/activated')} className="w-full mt-4 sm:mt-6 py-2 sm:py-2.5 text-[10px] sm:text-xs text-indigo-600 font-bold bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors">
                            View All
                        </button>
                    </div>

                    {/* Shop Orders */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-sm border border-gray-100 flex flex-col">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-900">Recent Orders</h3>
                                <p className="text-gray-500 text-[10px] sm:text-xs mt-1 font-medium">Shopping activity</p>
                            </div>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                                <Package size={18} />
                            </div>
                        </div>
                        <div className="space-y-3 sm:space-y-4 flex-1">
                            {stats?.recent_orders && stats.recent_orders.length > 0 ? (
                                stats.recent_orders.map((order, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => navigate('/orders')}>
                                        <div className="min-w-0">
                                            <p className="text-[9px] sm:text-[10px] font-bold text-blue-600 uppercase tracking-widest leading-none mb-1 truncate">{order.order_unique_id}</p>
                                            <p className="text-xs sm:text-sm font-black text-gray-900">₹{parseFloat(order.total_amount).toLocaleString()}</p>
                                        </div>
                                        <span className={`px-2 py-0.5 text-[9px] sm:text-[10px] font-bold rounded-lg border shrink-0 ${order.status === 'Delivered' ? 'bg-green-100 border-green-200 text-green-700' :
                                            order.status === 'Shipped' ? 'bg-blue-100 border-blue-200 text-blue-700' :
                                                'bg-yellow-100 border-yellow-200 text-yellow-700'
                                            }`}>
                                            {order.status}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="h-32 sm:h-40 flex flex-col items-center justify-center text-gray-400">
                                    <Package size={28} className="mb-2 opacity-20" />
                                    <p className="text-[10px] font-medium">No orders yet</p>
                                </div>
                            )}
                        </div>
                        <button onClick={() => navigate('/orders')} className="w-full mt-4 sm:mt-6 py-2 sm:py-2.5 text-[10px] sm:text-xs text-blue-600 font-bold bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
                            Manage Orders
                        </button>
                    </div>

                    {/* Recent Payouts */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-sm border border-gray-100 flex flex-col">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-900">Bank Payouts</h3>
                                <p className="text-gray-500 text-[10px] sm:text-xs mt-1 font-medium">Withdrawal status</p>
                            </div>
                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                                <IndianRupee size={18} />
                            </div>
                        </div>
                        <div className="space-y-3 sm:space-y-4 flex-1">
                            {stats?.recent_withdrawals && stats.recent_withdrawals.length > 0 ? (
                                stats.recent_withdrawals.map((payout, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2.5 sm:p-3 border border-gray-100 rounded-xl">
                                        <div>
                                            <p className="text-xs sm:text-sm font-black text-gray-900 leading-none mb-1">₹{parseFloat(payout.amount).toLocaleString()}</p>
                                            <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase">{payout.method}</p>
                                        </div>
                                        <span className={`px-2 py-0.5 text-[9px] sm:text-[10px] font-bold rounded-lg shrink-0 ${payout.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                            }`}>
                                            {payout.status}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="h-32 sm:h-40 flex flex-col items-center justify-center text-gray-400">
                                    <IndianRupee size={28} className="mb-2 opacity-20" />
                                    <p className="text-[10px] font-medium">No payouts yet</p>
                                </div>
                            )}
                        </div>
                        <button onClick={() => navigate('/income/wallet')} className="w-full mt-4 sm:mt-6 py-2 sm:py-2.5 text-[10px] sm:text-xs text-emerald-600 font-bold bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors">
                            Withdraw History
                        </button>
                    </div>
                </div>
            </div >
        </Layout >
    );
};


export default UserDashboard;
